import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  ArrowLeft, RefreshCw, Trash2, Save, Clock, User, GitBranch, Copy, 
  ExternalLink, Globe, Lock, Gamepad2, Map, Ship, Monitor, 
  AlertTriangle, History, ChevronLeft, ChevronRight, X, Video,
  CheckCircle, Check, Play, Wrench, RotateCcw, XCircle, MessageSquare, Cpu,
  BarChart3
} from "lucide-react";
import { SeverityBadge, StatusBadge } from "./Dashboard";
import { toast } from "sonner";
import { API } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { getLanguageName } from "@/i18n/translations";
import Comments from "@/components/Comments";
import BugTimeline from "@/components/BugTimeline";
import { SlideToConfirm } from "@/components/SlideToConfirm";

// Verification actions configuration
const VERIFICATION_ACTIONS = {
  replicate: { 
    label: "Mark Replicated", 
    icon: RefreshCw, 
    color: "bg-purple-500/20 text-purple-400 border-purple-500/30 hover:bg-purple-500/30",
    fromStatus: ["Needs Verification"]
  },
  verify: { 
    label: "Mark Verified", 
    icon: CheckCircle, 
    color: "bg-blue-500/20 text-blue-400 border-blue-500/30 hover:bg-blue-500/30",
    fromStatus: ["Replicated"]
  },
  start_progress: { 
    label: "Start Progress", 
    icon: Play, 
    color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30 hover:bg-yellow-500/30",
    fromStatus: ["Verified", "Replicated"]
  },
  mark_fixed: { 
    label: "Mark Fixed", 
    icon: Wrench, 
    color: "bg-green-500/20 text-green-400 border-green-500/30 hover:bg-green-500/30",
    fromStatus: ["In Progress"]
  },
  reopen: { 
    label: "Reopen", 
    icon: RotateCcw, 
    color: "bg-orange-500/20 text-orange-400 border-orange-500/30 hover:bg-orange-500/30",
    fromStatus: ["Fixed"]
  },
  reject: { 
    label: "Reject", 
    icon: XCircle, 
    color: "bg-red-500/20 text-red-400 border-red-500/30 hover:bg-red-500/30",
    fromStatus: ["Needs Verification", "Replicated", "Verified"]
  }
};

// Roles that can perform verification actions
const VERIFICATION_ROLES = ["admin", "developer", "tester"];

export default function BugDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isAuthenticated, user, login } = useAuth();
  const [bug, setBug] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [editStatus, setEditStatus] = useState("");
  const [editSeverity, setEditSeverity] = useState("");
  const [severities, setSeverities] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [verifyingAction, setVerifyingAction] = useState(null);
  const [discordExport, setDiscordExport] = useState(null);
  const [showDiscordExport, setShowDiscordExport] = useState(false);
  const [showFixedVersionPrompt, setShowFixedVersionPrompt] = useState(false);
  const [fixedInVersion, setFixedInVersion] = useState("");
  const [mergeSuggestion, setMergeSuggestion] = useState(null);
  const [settingCanonical, setSettingCanonical] = useState(false);
  const [showDeleteSlider, setShowDeleteSlider] = useState(false);
  const [isAffected, setIsAffected] = useState(false);
  const [affectedCount, setAffectedCount] = useState(0);
  const [markingAffected, setMarkingAffected] = useState(false);

  // Check if user can perform verification actions
  const canVerify = isAuthenticated && user && VERIFICATION_ROLES.includes(user.role?.toLowerCase());

  const fetchBug = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/bugs/${id}`);
      setBug(response.data);
      setEditStatus(response.data.status);
      setEditSeverity(response.data.severity);
    } catch (error) {
      console.error("Error fetching bug:", error);
      toast.error("Bug not found");
      navigate("/bugs");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchOptions = useCallback(async () => {
    try {
      const [sevRes, statRes] = await Promise.all([
        axios.get(`${API}/severities`),
        axios.get(`${API}/statuses`),
      ]);
      setSeverities(sevRes.data);
      setStatuses(statRes.data);
    } catch (error) {
      console.error("Error fetching options:", error);
    }
  }, []);

  const fetchMergeSuggestion = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/bugs/${id}/merge-suggestion`);
      if (response.data.has_suggestion) {
        setMergeSuggestion(response.data);
      }
    } catch (error) {
      console.error("Error fetching merge suggestion:", error);
    }
  }, [id]);

  const checkAffected = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/bugs/${id}/affected/check`, { withCredentials: true });
      setIsAffected(response.data.is_affected);
      setAffectedCount(response.data.affected_count);
    } catch (error) {
      // Expected when user is not authenticated (401) — do not surface to UI.
      // Log only for unexpected server errors to aid debugging.
      if (error.response?.status && error.response.status >= 500) {
        console.error("Error checking affected status:", error);
      }
    }
  }, [id]);

  const handleMarkAffected = async () => {
    if (!isAuthenticated) {
      toast.error("Please log in to mark this issue as affecting you");
      return;
    }
    try {
      setMarkingAffected(true);
      const response = await axios.post(`${API}/bugs/${id}/affected`, {}, { withCredentials: true });
      setIsAffected(true);
      setAffectedCount(response.data.affected_count);
      toast.success("Marked as affected");
    } catch (error) {
      if (error.response?.status === 409) {
        setIsAffected(true);
      } else if (error.response?.status === 401) {
        toast.error("Please log in to mark this issue as affecting you");
      } else {
        toast.error("Failed to mark as affected");
      }
    } finally {
      setMarkingAffected(false);
    }
  };

  useEffect(() => {
    fetchBug();
    fetchOptions();
    fetchMergeSuggestion();
    checkAffected();
  }, [fetchBug, fetchOptions, fetchMergeSuggestion, checkAffected]);

  const handleSetCanonical = async () => {
    if (!canVerify) return;
    try {
      setSettingCanonical(true);
      await axios.post(`${API}/bugs/${id}/set-canonical`, {}, { withCredentials: true });
      toast.success("Bug set as canonical — other cluster bugs merged into this one");
      fetchBug();
      setMergeSuggestion(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set as canonical");
    } finally {
      setSettingCanonical(false);
    }
  };

  const handleUpdate = async () => {
    if (!isAuthenticated) {
      toast.error(t('auth.notAuthorized'));
      return;
    }
    try {
      setUpdating(true);
      await axios.put(`${API}/bugs/${id}`, {
        status: editStatus,
        severity: editSeverity,
      }, {
        withCredentials: true
      });
      toast.success(t('common.success'));
      fetchBug();
    } catch (error) {
      console.error("Error updating bug:", error);
      if (error.response?.status === 401) {
        toast.error(t('auth.notAuthorized'));
      } else {
        toast.error(t('common.error'));
      }
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!isAuthenticated) {
      toast.error(t('auth.notAuthorized'));
      return;
    }
    try {
      await axios.delete(`${API}/bugs/${id}`, {
        withCredentials: true
      });
      toast.success("Bug deleted");
      navigate("/bugs");
    } catch (error) {
      console.error("Error deleting bug:", error);
      if (error.response?.status === 401) {
        toast.error(t('auth.notAuthorized'));
      } else {
        toast.error(t('common.error'));
      }
      setShowDeleteSlider(false);
    }
  };

  const copyDisplayCode = () => {
    navigator.clipboard.writeText(bug.display_code || bug.id);
    toast.success("Bug code copied to clipboard");
  };

  // Verification action handler
  const handleVerificationAction = async (action, extraData = {}) => {
    if (!canVerify) {
      toast.error("You don't have permission to perform this action");
      return;
    }

    // If marking as fixed, show version prompt first
    if (action === "mark_fixed" && !extraData.fixed_in_version) {
      setShowFixedVersionPrompt(true);
      return;
    }

    try {
      setVerifyingAction(action);
      const payload = {
        action: action,
        notes: null,
        ...extraData
      };
      await axios.post(`${API}/bugs/${id}/verify`, payload, {
        withCredentials: true
      });
      toast.success(`Bug ${action.replace('_', ' ')} successfully`);
      fetchBug(); // Refresh bug data
    } catch (error) {
      console.error("Error performing verification action:", error);
      if (error.response?.status === 401) {
        toast.error("Authentication required");
      } else if (error.response?.status === 403) {
        toast.error("You don't have permission to perform this action");
      } else {
        toast.error(error.response?.data?.detail || "Failed to perform action");
      }
    } finally {
      setVerifyingAction(null);
    }
  };

  // Confirm mark as fixed with version
  const confirmMarkFixed = () => {
    setShowFixedVersionPrompt(false);
    handleVerificationAction("mark_fixed", { fixed_in_version: fixedInVersion || null });
    setFixedInVersion("");
  };

  // Discord export handler
  const handleDiscordExport = async () => {
    if (!canVerify) {
      toast.error("You don't have permission to export to Discord");
      return;
    }

    try {
      const response = await axios.get(`${API}/bugs/${id}/discord-export`, {
        withCredentials: true
      });
      setDiscordExport(response.data);
      setShowDiscordExport(true);
    } catch (error) {
      console.error("Error fetching Discord export:", error);
      if (error.response?.status === 401) {
        toast.error("Authentication required");
      } else {
        toast.error("Failed to generate Discord export");
      }
    }
  };

  // Copy Discord embed JSON
  const copyDiscordEmbed = () => {
    if (discordExport?.embed) {
      navigator.clipboard.writeText(JSON.stringify(discordExport.embed, null, 2));
      toast.success("Discord embed JSON copied to clipboard");
    }
  };

  // Copy plain text version
  const copyPlainText = () => {
    if (discordExport?.plain_text) {
      navigator.clipboard.writeText(discordExport.plain_text);
      toast.success("Plain text copied to clipboard");
    }
  };

  // Get available actions based on current status
  const getAvailableActions = () => {
    if (!bug) return [];
    return Object.entries(VERIFICATION_ACTIONS).filter(([key, action]) => 
      action.fromStatus.includes(bug.status)
    );
  };

  const openImageLightbox = (index) => {
    setCurrentImageIndex(index);
    setSelectedImage(bug.images[index]);
  };

  const navigateImage = (direction) => {
    const newIndex = direction === 'prev' 
      ? (currentImageIndex - 1 + bug.images.length) % bug.images.length
      : (currentImageIndex + 1) % bug.images.length;
    setCurrentImageIndex(newIndex);
    setSelectedImage(bug.images[newIndex]);
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8 flex items-center justify-center min-h-screen">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!bug) return null;

  return (
    <div className="p-6 md:p-8 animate-fade-in" data-testid="bug-detail-page">
      {/* Image Lightbox with Navigation */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setSelectedImage(null); }}
            className="absolute top-4 right-4 p-2 text-white/70 hover:text-white"
          >
            <X className="w-8 h-8" />
          </button>
          
          {bug.images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); navigateImage('prev'); }}
                className="absolute left-4 p-2 text-white/70 hover:text-white bg-black/50 rounded-full"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); navigateImage('next'); }}
                className="absolute right-4 p-2 text-white/70 hover:text-white bg-black/50 rounded-full"
              >
                <ChevronRight className="w-8 h-8" />
              </button>
            </>
          )}
          
          <img 
            src={selectedImage} 
            alt={`Image ${currentImageIndex + 1}`}
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          
          {bug.images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white font-mono text-sm">
              {currentImageIndex + 1} / {bug.images.length}
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <div className="mb-6 md:mb-8">
        <Link
          to="/bugs"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 font-mono text-sm"
          data-testid="back-to-bugs"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('bugDetail.backToBugs')}
        </Link>
        
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {/* Display Code */}
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <button 
                onClick={copyDisplayCode} 
                className="font-mono text-xs text-primary hover:text-primary/80 flex items-center gap-1 bg-primary/10 px-2 py-1 rounded-sm border border-primary/20"
                data-testid="copy-code-btn"
              >
                {bug.display_code || `#${bug.id.slice(0, 8)}`}
                <Copy className="w-3 h-3" />
              </button>
              <SeverityBadge severity={bug.severity} />
              <StatusBadge status={bug.status} />
              {bug.reopened && (
                <span className="px-2 py-0.5 bg-orange-500/10 text-orange-400 border border-orange-500/20 rounded-none font-mono text-[10px] uppercase flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  REOPENED
                </span>
              )}
              {bug.original_language && bug.original_language !== 'en' && (
                <span className="px-2 py-0.5 bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 rounded-none font-mono text-[10px] uppercase flex items-center gap-1">
                  <Globe className="w-3 h-3" />
                  {getLanguageName(bug.original_language)}
                </span>
              )}
              {/* Game Badge */}
              {bug.game === "STAR_ATLAS_HOLOSIM" ? (
                <span className="px-2 py-0.5 bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 rounded-none font-mono text-[10px] uppercase flex items-center gap-1">
                  <Globe className="w-3 h-3" />
                  HOLOSIM
                </span>
              ) : (
                <span className="px-2 py-0.5 bg-primary/10 text-primary border border-primary/20 rounded-none font-mono text-[10px] uppercase flex items-center gap-1">
                  <Cpu className="w-3 h-3" />
                  UE5
                </span>
              )}
            </div>
            <h1 className="font-heading text-xl md:text-2xl font-bold uppercase tracking-wider text-foreground break-words">
              {bug.title}
            </h1>
            <div className="cyber-line mt-2" />
          </div>

          {/* Delete button - only for authenticated users */}
          {isAuthenticated && (
            <div className="flex flex-col gap-2 flex-shrink-0 items-end">
              {showDeleteSlider ? (
                <div className="w-52">
                  <SlideToConfirm
                    onConfirm={handleDelete}
                    label="Slide to delete"
                    variant="destructive"
                  />
                </div>
              ) : (
                <Button variant="outline" onClick={() => setShowDeleteSlider(true)} className="text-destructive hover:bg-destructive/10" data-testid="delete-bug-btn">
                  <Trash2 className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">{t('bugDetail.delete')}</span>
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Verification Actions - Only for Testers, Developers, Admins */}
        {canVerify && getAvailableActions().length > 0 && (
          <Card className="bg-card border-border/40 mb-6">
            <CardHeader className="border-b border-border/40 bg-muted/20 p-3">
              <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Verification Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <div className="flex flex-wrap gap-2">
                {getAvailableActions().map(([action, config]) => {
                  const IconComponent = config.icon;
                  return (
                    <Button
                      key={action}
                      variant="outline"
                      size="sm"
                      onClick={() => handleVerificationAction(action)}
                      disabled={verifyingAction === action}
                      className={`${config.color} font-mono text-xs`}
                      data-testid={`verify-${action}-btn`}
                    >
                      {verifyingAction === action ? (
                        <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        <IconComponent className="w-3 h-3 mr-1" />
                      )}
                      {config.label}
                    </Button>
                  );
                })}
                
                {/* Discord Export Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDiscordExport}
                  className="bg-indigo-500/20 text-indigo-400 border-indigo-500/30 hover:bg-indigo-500/30 font-mono text-xs ml-auto"
                  data-testid="discord-export-btn"
                >
                  <MessageSquare className="w-3 h-3 mr-1" />
                  Copy to Discord
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Fixed Version Prompt */}
        {showFixedVersionPrompt && (
          <div 
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setShowFixedVersionPrompt(false)}
          >
            <Card 
              className="bg-card border-border/40 max-w-sm w-full"
              onClick={(e) => e.stopPropagation()}
              data-testid="fixed-version-modal"
            >
              <CardHeader className="border-b border-border/40 bg-green-500/10 p-4">
                <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-green-400 flex items-center gap-2">
                  <Wrench className="w-4 h-4" />
                  Mark as Fixed
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div>
                  <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">
                    Fixed in Version
                  </label>
                  <input
                    type="text"
                    value={fixedInVersion}
                    onChange={(e) => setFixedInVersion(e.target.value)}
                    placeholder="e.g. v1.2.13"
                    className="w-full px-3 py-2 bg-black/20 border border-border/50 rounded-sm text-foreground font-mono text-sm focus:outline-none focus:border-green-500/50"
                    data-testid="fixed-version-input"
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && confirmMarkFixed()}
                  />
                  <p className="text-[10px] text-muted-foreground mt-1 font-mono">Optional — leave blank if unknown</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={confirmMarkFixed}
                    className="flex-1 bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30 font-mono text-xs"
                    data-testid="confirm-mark-fixed-btn"
                  >
                    <Wrench className="w-3 h-3 mr-1" />
                    Confirm Fixed
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowFixedVersionPrompt(false)}
                    className="font-mono text-xs"
                    data-testid="cancel-mark-fixed-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Discord Export Modal */}
        {showDiscordExport && discordExport && (
          <div 
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setShowDiscordExport(false)}
          >
            <Card 
              className="bg-card border-border/40 max-w-2xl w-full max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <CardHeader className="border-b border-border/40 bg-indigo-500/10 p-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading text-lg font-bold uppercase tracking-wider text-indigo-400 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    Discord Export
                  </CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setShowDiscordExport(false)}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-4 overflow-y-auto max-h-[60vh]">
                <div className="space-y-4">
                  {/* Plain Text Version */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs text-muted-foreground font-mono uppercase">Plain Text (Copy & Paste)</label>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={copyPlainText}
                        className="font-mono text-xs"
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        Copy
                      </Button>
                    </div>
                    <pre className="bg-black/30 p-3 rounded-sm text-xs text-muted-foreground overflow-x-auto whitespace-pre-wrap max-h-48 overflow-y-auto border border-border/40">
                      {discordExport.plain_text}
                    </pre>
                  </div>

                  {/* Embed JSON Version */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-xs text-muted-foreground font-mono uppercase">Discord Embed JSON (For Bot)</label>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={copyDiscordEmbed}
                        className="font-mono text-xs"
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        Copy JSON
                      </Button>
                    </div>
                    <pre className="bg-black/30 p-3 rounded-sm text-xs text-green-400/80 overflow-x-auto max-h-48 overflow-y-auto border border-border/40">
                      {JSON.stringify(discordExport.embed, null, 2)}
                    </pre>
                  </div>

                  {/* Direct Link */}
                  <div className="pt-2 border-t border-border/40">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground font-mono">Bug URL:</span>
                      <a 
                        href={discordExport.bug_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline font-mono flex items-center gap-1"
                      >
                        {discordExport.bug_url}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Merge Suggestion Banner */}
      {mergeSuggestion && canVerify && (
        <Card className="bg-emerald-500/5 border-emerald-500/20 mb-6" data-testid="merge-suggestion-banner">
          <CardContent className="p-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <GitBranch className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              <p className="text-xs text-emerald-300 font-mono truncate">
                A newer version exists: <Link to={`/bugs/${mergeSuggestion.suggested_canonical_id}`} className="underline text-emerald-200">{mergeSuggestion.suggested_canonical_code}</Link> — {mergeSuggestion.reason}
              </p>
            </div>
            <Link
              to="/admin"
              className="text-[10px] px-2 py-1 bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded-sm hover:bg-emerald-500/30 font-mono flex-shrink-0"
            >
              View Cluster
            </Link>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <Card className="bg-card border-border/40">
            <CardHeader className="border-b border-border/40 bg-muted/20 p-3 md:p-4">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-base md:text-lg font-bold uppercase tracking-wider text-muted-foreground">
                  {t('bugDetail.description')}
                </CardTitle>
                <div className="flex items-center gap-3">
                  {(affectedCount > 0 || bug.affected_count > 0) && (
                    <span className="text-[10px] text-muted-foreground font-mono" data-testid="affected-count">
                      {affectedCount || bug.affected_count || 0} affected
                    </span>
                  )}
                  {isAffected ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-sm font-mono text-xs font-bold" data-testid="affected-confirmed-btn">
                      <Check className="w-4 h-4" />
                      You are affected
                    </span>
                  ) : (
                    <Button
                      onClick={handleMarkAffected}
                      disabled={markingAffected}
                      size="sm"
                      className="h-8 px-4 text-xs font-mono font-bold bg-primary text-primary-foreground hover:bg-primary/90 border border-primary/50 shadow-sm shadow-primary/20"
                      data-testid="mark-affected-btn"
                    >
                      {markingAffected ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}
                      I have this issue
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-3 md:p-4">
              <p className="text-foreground font-body leading-relaxed text-sm md:text-base">{bug.description}</p>
            </CardContent>
          </Card>

          {/* Game Location */}
          <Card className="bg-card border-border/40">
            <CardHeader className="border-b border-border/40 bg-muted/20 p-3 md:p-4">
              <CardTitle className="font-heading text-base md:text-lg font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                <Gamepad2 className="w-5 h-5" />
                Game Location
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 md:p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground font-mono uppercase mb-1">Category</p>
                  <p className="text-sm text-foreground font-body">{bug.category || "N/A"}</p>
                </div>
                {bug.mode && (
                  <div>
                    <p className="text-xs text-muted-foreground font-mono uppercase mb-1">Mode</p>
                    <p className="text-sm text-foreground font-body">{bug.mode}</p>
                  </div>
                )}
                {bug.maps?.length > 0 && (
                  <div>
                    <p className="text-xs text-muted-foreground font-mono uppercase mb-1 flex items-center gap-1">
                      <Map className="w-3 h-3" />
                      Maps
                    </p>
                    <p className="text-sm text-foreground font-body">{bug.maps.join(", ")}</p>
                  </div>
                )}
                {bug.mission && (
                  <div>
                    <p className="text-xs text-muted-foreground font-mono uppercase mb-1">Mission</p>
                    <p className="text-sm text-foreground font-body">{bug.mission}</p>
                  </div>
                )}
                {bug.season && (
                  <div>
                    <p className="text-xs text-muted-foreground font-mono uppercase mb-1">Season</p>
                    <p className="text-sm text-foreground font-body">{bug.season}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* AI Analysis - Root Cause & Suggested Fix - Only visible to Admin/Developer/Tester */}
          {(bug.ai_root_cause || bug.ai_suggested_fix) && isAuthenticated && user && ['admin', 'developer', 'tester'].includes(user.role?.toLowerCase()) && (
            <Card className="bg-card border-border/40 border-l-4 border-l-purple-500" data-testid="ai-analysis-section">
              <CardHeader className="border-b border-border/40 bg-purple-500/5 p-3 md:p-4">
                <CardTitle className="font-heading text-base md:text-lg font-bold uppercase tracking-wider text-purple-400 flex items-center gap-2">
                  <span className="text-lg">🤖</span>
                  AI Analysis
                  <span className="text-[10px] text-muted-foreground font-normal ml-auto">(Internal)</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 md:p-4 space-y-4">
                {bug.ai_root_cause && (
                  <div>
                    <p className="text-xs text-purple-400 font-mono uppercase mb-2 font-bold">Root Cause Analysis</p>
                    <p className="text-sm text-foreground font-body bg-purple-500/5 p-3 rounded-sm border border-purple-500/20">{bug.ai_root_cause}</p>
                  </div>
                )}
                {bug.ai_suggested_fix && (
                  <div>
                    <p className="text-xs text-emerald-400 font-mono uppercase mb-2 font-bold">Suggested Fix</p>
                    <p className="text-sm text-foreground font-body bg-emerald-500/5 p-3 rounded-sm border border-emerald-500/20">{bug.ai_suggested_fix}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Steps to Reproduce */}
          <Card className="bg-card border-border/40">
            <CardHeader className="border-b border-border/40 bg-muted/20 p-3 md:p-4">
              <CardTitle className="font-heading text-base md:text-lg font-bold uppercase tracking-wider text-muted-foreground">
                {t('bugDetail.stepsToReproduce')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 md:p-4">
              {bug.steps_to_reproduce && bug.steps_to_reproduce.length > 0 ? (
                <ol className="list-decimal list-inside space-y-2">
                  {bug.steps_to_reproduce.map((step, i) => (
                    <li key={`${bug.id}-step-${i}-${step?.slice(0, 20)}`} className="text-foreground font-mono text-xs md:text-sm">{step}</li>
                  ))}
                </ol>
              ) : (
                <p className="text-muted-foreground font-mono text-xs md:text-sm">{t('bugDetail.noStepsProvided')}</p>
              )}
            </CardContent>
          </Card>

          {/* Expected Result */}
          {bug.expected_result && (
            <Card className="bg-card border-border/40">
              <CardHeader className="border-b border-border/40 bg-emerald-500/5 p-3 md:p-4">
                <CardTitle className="font-heading text-sm font-bold uppercase tracking-wider text-emerald-400">
                  {t('bugDetail.expectedResult')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 md:p-4">
                <p className="text-foreground font-body text-xs md:text-sm">{bug.expected_result}</p>
              </CardContent>
            </Card>
          )}

          {/* Ship Information */}
          {(bug.ship_model || bug.ship_size) && (
            <Card className="bg-card border-border/40">
              <CardHeader className="border-b border-border/40 bg-muted/20 p-3 md:p-4">
                <CardTitle className="font-heading text-base md:text-lg font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                  <Ship className="w-5 h-5" />
                  Ship Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 md:p-4">
                <div className="grid grid-cols-2 gap-4">
                  {bug.ship_model && (
                    <div>
                      <p className="text-xs text-muted-foreground font-mono uppercase mb-1">Model</p>
                      <p className="text-sm text-foreground font-body">{bug.ship_model}</p>
                    </div>
                  )}
                  {bug.ship_size && (
                    <div>
                      <p className="text-xs text-muted-foreground font-mono uppercase mb-1">Size</p>
                      <p className="text-sm text-foreground font-body">{bug.ship_size}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Environment */}
          {bug.environment && (bug.environment.pc || bug.environment.windows_version || bug.environment.gpu || bug.environment.ram) && (
            <Card className="bg-card border-border/40">
              <CardHeader className="border-b border-border/40 bg-muted/20 p-3 md:p-4">
                <CardTitle className="font-heading text-base md:text-lg font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                  <Monitor className="w-5 h-5" />
                  Environment
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 md:p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {bug.environment.pc && (
                    <div>
                      <p className="text-xs text-muted-foreground font-mono uppercase mb-1">PC</p>
                      <p className="text-sm text-foreground font-mono">{bug.environment.pc}</p>
                    </div>
                  )}
                  {bug.environment.windows_version && (
                    <div>
                      <p className="text-xs text-muted-foreground font-mono uppercase mb-1">Windows</p>
                      <p className="text-sm text-foreground font-mono">{bug.environment.windows_version}</p>
                    </div>
                  )}
                  {bug.environment.gpu && (
                    <div>
                      <p className="text-xs text-muted-foreground font-mono uppercase mb-1">GPU</p>
                      <p className="text-sm text-foreground font-mono">{bug.environment.gpu}</p>
                    </div>
                  )}
                  {bug.environment.ram && (
                    <div>
                      <p className="text-xs text-muted-foreground font-mono uppercase mb-1">RAM</p>
                      <p className="text-sm text-foreground font-mono">{bug.environment.ram}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Attached Media */}
          {((bug.images && bug.images.length > 0) || bug.video_url) && (
            <Card className="bg-card border-border/40">
              <CardHeader className="border-b border-border/40 bg-muted/20 p-3 md:p-4">
                <CardTitle className="font-heading text-base md:text-lg font-bold uppercase tracking-wider text-muted-foreground">
                  Media Attachments
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 md:p-4">
                {/* Images with navigation */}
                {bug.images && bug.images.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs text-muted-foreground font-mono uppercase mb-2">
                      Images ({bug.images.length})
                    </p>
                    <div className="flex flex-wrap gap-3 md:gap-4">
                      {bug.images.map((img, i) => (
                        <button
                          key={`${bug.id}-img-${i}-${(img || '').slice(0, 32)}`}
                          onClick={() => openImageLightbox(i)}
                          className="relative group"
                        >
                          <img
                            src={img}
                            alt={`Attachment ${i + 1}`}
                            className="w-24 h-24 md:w-32 md:h-32 object-cover rounded-sm border border-border/40 hover:border-primary/50 transition-colors cursor-pointer"
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-sm">
                            <span className="text-xs text-white font-mono">View</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Video */}
                {bug.video_url && (
                  <div>
                    <p className="text-xs text-muted-foreground font-mono uppercase mb-2 flex items-center gap-1">
                      <Video className="w-3 h-3" />
                      Video
                    </p>
                    {bug.video_url.startsWith("data:video") ? (
                      <video
                        src={bug.video_url}
                        controls
                        className="w-full max-h-64 rounded-sm border border-border/40 bg-black"
                        data-testid="bug-video-player"
                      />
                    ) : (
                      <a
                        href={bug.video_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:text-primary/80 font-mono break-all flex items-center gap-2"
                      >
                        <ExternalLink className="w-4 h-4 flex-shrink-0" />
                        {bug.video_url}
                      </a>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Bug Timeline - Only visible to Admin/Developer/Tester */}
          {isAuthenticated && user && ['admin', 'developer', 'tester'].includes(user.role?.toLowerCase()) && (
            <BugTimeline bugId={id} createdAt={bug.created_at} />
          )}

          {/* Raw Report */}
          {bug.raw_report && (
            <Card className="bg-card border-border/40">
              <CardHeader className="border-b border-border/40 bg-muted/20 p-3 md:p-4">
                <CardTitle className="font-heading text-base md:text-lg font-bold uppercase tracking-wider text-muted-foreground">
                  {t('bugDetail.originalReport')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 md:p-4">
                <pre className="font-mono text-xs text-muted-foreground bg-black/30 p-3 md:p-4 rounded-sm overflow-x-auto whitespace-pre-wrap">
                  {bug.raw_report}
                </pre>
              </CardContent>
            </Card>
          )}

          {/* Comments Section moved to sidebar */}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Edit - Only for authenticated users */}
          <Card className="bg-card border-border/40">
            <CardHeader className="border-b border-border/40 bg-muted/20 p-3 md:p-4">
              <CardTitle className="font-heading text-base md:text-lg font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                {t('bugDetail.quickEdit')}
                {!isAuthenticated && <Lock className="w-4 h-4 text-muted-foreground" />}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 md:p-4 space-y-4">
              {!isAuthenticated ? (
                <div className="text-center py-4">
                  <Lock className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground font-body mb-4">
                    {t('auth.devOnly')}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={login}
                    className="font-mono text-xs"
                    data-testid="login-to-edit-btn"
                  >
                    {t('auth.loginBtn')}
                  </Button>
                </div>
              ) : (
                <>
                  <div>
                    <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">{t('bugList.severity')}</label>
                    <Select value={editSeverity} onValueChange={setEditSeverity}>
                      <SelectTrigger className="bg-black/20 border-border/50 font-mono" data-testid="edit-severity">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {severities.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2 block">{t('bugList.status')}</label>
                    <Select value={editStatus} onValueChange={setEditStatus}>
                      <SelectTrigger className="bg-black/20 border-border/50 font-mono" data-testid="edit-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {statuses.filter(s => s !== "Merged").map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    onClick={handleUpdate}
                    disabled={updating}
                    className="w-full font-heading font-bold uppercase tracking-wider"
                    data-testid="save-changes-btn"
                  >
                    {updating ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    {t('bugDetail.saveChanges')}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {/* Metadata */}
          <Card className="bg-card border-border/40">
            <CardHeader className="border-b border-border/40 bg-muted/20 p-3 md:p-4">
              <CardTitle className="font-heading text-base md:text-lg font-bold uppercase tracking-wider text-muted-foreground">
                {t('bugDetail.metadata')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 md:p-4 space-y-3">
              <div className="flex items-center gap-3">
                <GitBranch className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground font-mono uppercase">{t('bugList.classification')}</p>
                  <p className="text-sm text-foreground font-body">{bug.classification}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground font-mono uppercase">{t('bugDetail.version')}</p>
                  <p className="text-sm text-foreground font-mono">{bug.version || "Unknown"}</p>
                </div>
              </div>

              {bug.fixed_in_version && (
                <div className="flex items-center gap-3" data-testid="fixed-in-version">
                  <Wrench className="w-4 h-4 text-green-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-green-400 font-mono uppercase">Fixed in Version</p>
                    <p className="text-sm text-green-300 font-mono font-bold">{bug.fixed_in_version}</p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <RefreshCw className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground font-mono uppercase">Frequency</p>
                  <p className="text-sm text-foreground font-body">{bug.frequency || "Sometimes"}</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <ExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground font-mono uppercase">{t('bugDetail.source')}</p>
                  <p className="text-sm text-foreground font-body">{bug.source}</p>
                </div>
              </div>

              {bug.original_language && (
                <div className="flex items-center gap-3">
                  <Globe className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground font-mono uppercase">{t('bugDetail.originalLanguage')}</p>
                    <p className="text-sm text-foreground font-body">{getLanguageName(bug.original_language)}</p>
                  </div>
                </div>
              )}

              {bug.discord_user && (
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground font-mono uppercase">{t('bugDetail.discordUser')}</p>
                    <p className="text-sm text-foreground font-mono truncate">{bug.discord_user}</p>
                  </div>
                </div>
              )}

              {/* Holosim Player Name - visible to all */}
              {bug.holosim_player_name && (
                <div className="flex items-center gap-3" data-testid="holosim-player-name">
                  <Gamepad2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground font-mono uppercase">Player Name</p>
                    <p className="text-sm text-foreground font-mono truncate">{bug.holosim_player_name}</p>
                  </div>
                </div>
              )}

              {/* Holosim Game ID + Profile - Admin/Dev only */}
              {bug.holosim_game_id && user && (user.role === "admin" || user.role === "developer") && (
                <div className="space-y-2 border-l-2 border-amber-400/30 pl-3" data-testid="holosim-admin-fields">
                  <div className="flex items-center gap-3">
                    <Lock className="w-4 h-4 text-amber-400/60 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-amber-400/60 font-mono uppercase">Game ID (Admin)</p>
                      <p className="text-sm text-foreground font-mono truncate">{bug.holosim_game_id}</p>
                    </div>
                  </div>
                  {bug.holosim_player_profile && (
                    <div className="flex items-center gap-3">
                      <Lock className="w-4 h-4 text-amber-400/60 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs text-amber-400/60 font-mono uppercase">Player Profile (Admin)</p>
                        <p className="text-sm text-foreground font-mono truncate">{bug.holosim_player_profile}</p>
                      </div>
                    </div>
                  )}
                  {bug.holosim_fleets && bug.holosim_fleets.length > 0 && (
                    <div className="flex items-center gap-3">
                      <Ship className="w-4 h-4 text-amber-400/60 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs text-amber-400/60 font-mono uppercase">Fleets (Admin)</p>
                        <p className="text-sm text-foreground font-mono">{bug.holosim_fleets.length} fleet(s)</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Impact Score */}
              {bug.impact_score > 0 && (
                <div className="flex items-center gap-3">
                  <BarChart3 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-muted-foreground font-mono uppercase">Impact Score</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all ${
                            bug.impact_score >= 80 ? 'bg-red-500' : 
                            bug.impact_score >= 60 ? 'bg-orange-500' : 
                            bug.impact_score >= 40 ? 'bg-yellow-500' : 'bg-blue-500'
                          }`}
                          style={{ width: `${bug.impact_score}%` }}
                        />
                      </div>
                      <span className={`text-sm font-mono font-bold ${
                        bug.impact_score >= 80 ? 'text-red-400' : 
                        bug.impact_score >= 60 ? 'text-orange-400' : 
                        bug.impact_score >= 40 ? 'text-yellow-400' : 'text-blue-400'
                      }`}>
                        {bug.impact_score}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {bug.discord_message_link && (
                <div className="mt-2">
                  <a
                    href={bug.discord_message_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:text-primary/80 font-mono flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" />
                    View Original Discord Message
                  </a>
                </div>
              )}

              {bug.duplicate_of && (
                <div className="mt-4 p-3 bg-purple-500/10 border border-purple-500/20 rounded-sm">
                  <p className="text-xs text-purple-400 font-mono uppercase mb-1">{t('bugDetail.duplicateOf')}</p>
                  <Link
                    to={`/bugs/${bug.duplicate_of}`}
                    className="text-sm text-purple-300 hover:text-purple-200 font-mono underline flex items-center gap-2"
                  >
                    <span className="px-2 py-0.5 bg-purple-500/20 border border-purple-500/30 rounded-sm">
                      {bug.duplicate_of_display_code || 'View Original'}
                    </span>
                    View Original Bug
                  </Link>
                </div>
              )}

              {bug.duplicate_count > 0 && (
                <div className="mt-4 p-3 bg-orange-500/10 border border-orange-500/20 rounded-sm">
                  <p className="text-xs text-orange-400 font-mono uppercase mb-1">{t('bugDetail.duplicateCount')}</p>
                  <p className="text-lg text-orange-300 font-heading font-bold">{bug.duplicate_count}</p>
                  {bug.linked_duplicates_display_codes?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-muted-foreground font-mono mb-1">Linked Duplicates (newest first):</p>
                      <div className="flex flex-wrap gap-1">
                        {[...bug.linked_duplicates_display_codes].reverse().slice(0, 5).map((code, i) => {
                          const reversedIds = [...(bug.linked_duplicates || [])].reverse();
                          return (
                            <Link
                              key={code}
                              to={`/bugs/${reversedIds[i] || ''}`}
                              className="text-[10px] px-1.5 py-0.5 bg-orange-500/20 text-orange-300 border border-orange-500/30 rounded-sm hover:bg-orange-500/30 font-mono"
                            >
                              {code}
                            </Link>
                          );
                        })}
                        {bug.linked_duplicates_display_codes.length > 5 && (
                          <span className="text-[10px] text-muted-foreground">
                            +{bug.linked_duplicates_display_codes.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {bug.previous_bug_id && (
                <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-sm">
                  <p className="text-xs text-yellow-400 font-mono uppercase mb-1">Regression Of</p>
                  <Link
                    to={`/bugs/${bug.previous_bug_id}`}
                    className="text-sm text-yellow-300 hover:text-yellow-200 font-mono underline"
                  >
                    View Previous Bug
                  </Link>
                </div>
              )}

              {/* Merged Into */}
              {bug.merged_into_bug_id && (
                <div className="mt-4 p-3 bg-slate-500/10 border border-slate-500/20 rounded-sm" data-testid="merged-into-info">
                  <p className="text-xs text-slate-400 font-mono uppercase mb-1">Merged Into</p>
                  <Link
                    to={`/bugs/${bug.merged_into_bug_id}`}
                    className="text-sm text-slate-300 hover:text-slate-200 font-mono underline flex items-center gap-2"
                  >
                    <GitBranch className="w-3 h-3" />
                    View Canonical Bug
                  </Link>
                </div>
              )}

              {/* Set as Canonical button */}
              {canVerify && bug.duplicate_cluster_id && bug.merged_into_bug_id && (
                <div className="mt-3">
                  <Button
                    onClick={handleSetCanonical}
                    disabled={settingCanonical}
                    variant="outline"
                    size="sm"
                    className="w-full bg-emerald-500/10 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20 font-mono text-xs"
                    data-testid="set-canonical-btn"
                  >
                    {settingCanonical ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <GitBranch className="w-3 h-3 mr-1" />}
                    Set as Canonical
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Comments Section */}
          <Comments bugId={id} />
        </div>
      </div>
    </div>
  );
}
